<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { X } from 'lucide-vue-next';
import type { FilterWrapperProperties } from '@optionway/models';

withDefaults(defineProps<FilterWrapperProperties>(), {
  hasActiveFilters: false,
  disabled: false,
});

const emit = defineEmits<{ reset: [] }>();

const isOpen = ref(false);

const filterTriggerReference = useTemplateRef<ComponentPublicInstance>('trigger');

watch(isOpen, (opened) => {
  if (!opened) return;
  const element: unknown = filterTriggerReference.value?.$el;
  if (element instanceof Element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
});
</script>

<template>
  <UiSheet v-model:open="isOpen">
    <UiSheetTrigger as-child>
      <UiCustomFilterTrigger
        ref="trigger"
        :variant="disabled ? 'disabled' : hasActiveFilters ? 'active' : 'default'"
        :is-opened="isOpen"
      >
        {{ triggerButtonLabel }}
      </UiCustomFilterTrigger>
    </UiSheetTrigger>
    <UiSheetContent
      side="bottom"
      class="flex max-h-sheet flex-col gap-0 overflow-hidden p-0"
      :hide-close="true"
    >
      <UiSheetHeader class="h-16.5 flex-row items-center justify-between border-b p-4">
        <UiSheetTitle class="text-xl">{{ triggerButtonLabel }}</UiSheetTitle>
        <UiSheetDescription class="sr-only">{{ triggerButtonLabel }}</UiSheetDescription>
        <UiSheetClose :aria-label="$t('components.common.close')">
          <X
            class="h-5 w-5"
            aria-hidden="true"
          />
        </UiSheetClose>
      </UiSheetHeader>
      <div class="flex flex-col gap-4 overflow-y-auto p-4">
        <slot />
      </div>
      <div class="flex gap-2 border-t p-4">
        <UiButton
          variant="tertiary"
          color="gray"
          class="flex-1 hover:bg-transparent"
          :disabled="!hasActiveFilters"
          @click="emit('reset')"
        >
          {{ $t('components.results.filters.reset') }}
        </UiButton>
        <UiSheetClose as-child>
          <UiButton class="flex-1">
            {{ $t('components.results.filters.showResults') }}
            (<span aria-live="polite">{{ resultsCount ?? 0 }}</span
            >)
          </UiButton>
        </UiSheetClose>
      </div>
    </UiSheetContent>
  </UiSheet>
</template>
